import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import {
  Box,
  Button,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useGetUserQuery } from 'api';
import { useIsAdminImpersonating } from 'hooks/slices/useCurrentUser';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import { useAuthStore } from 'stores/authStore';
import type { User } from 'types/models';
import { userUtils } from 'utils/userUtils';

const BannerContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.warning.lighter,
  color: theme.palette.warning.main,
  borderRadius: '8px',
  padding: theme.spacing(2),
}));

type CollapsedImpersonationBannerProps = {
  onStop: VoidFunction;
};

const CollapsedImpersonationBanner = ({ onStop }: CollapsedImpersonationBannerProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
      <Tooltip title={t`Arrêter l'impersonification`} placement="right">
        <IconButton
          onClick={onStop}
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.lighter,
            '&:hover': {
              backgroundColor: theme.palette.warning.dark,
            },
          }}
        >
          <PersonOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

type ExpandedImpersonationBannerProps = {
  trueUserName?: string;
  currentUser: Pick<User, 'first_name' | 'last_name'> | null;
  onStop: VoidFunction;
};

const ExpandedImpersonationBanner = ({
  trueUserName,
  currentUser,
  onStop,
}: ExpandedImpersonationBannerProps) => {
  const theme = useTheme();

  return (
    <BannerContainer sx={{ m: 2 }}>
      <Box>
        <Box sx={{ mb: 1 }}>
          <Typography>
            <Trans>
              <strong>{trueUserName}</strong> impersonifie{' '}
              <strong>{userUtils.getFullName(currentUser)}</strong>
            </Trans>
          </Typography>
        </Box>
        <Button
          color="warning"
          variant="contained"
          fullWidth
          onClick={onStop}
          sx={{ color: theme.palette.warning.lighter }}
        >
          {t`Arrêter`}
        </Button>
      </Box>
    </BannerContainer>
  );
};

export const OptImpersonationBanner = () => {
  const { isCollapse } = useCollapseDrawer();
  const navigate = useNavigate();
  const authToken = useAuthStore((state) => state.userToken);
  const clearImpersonatedUser = useAuthStore((state) => state.clearImpersonatedUser);

  const getUserQuery = useGetUserQuery({ enabled: !!authToken });
  const currentUser = getUserQuery.data?.user || null;
  const trueUserName = getUserQuery.data?.meta?.true_user_name;

  const isAdminImpersonating = useIsAdminImpersonating();

  const handleStopImpersonating = () => {
    clearImpersonatedUser();
    navigate({ to: '/' });
  };

  if (!isAdminImpersonating) return null;

  if (isCollapse) {
    return <CollapsedImpersonationBanner onStop={handleStopImpersonating} />;
  }

  return (
    <ExpandedImpersonationBanner
      trueUserName={trueUserName}
      currentUser={currentUser}
      onStop={handleStopImpersonating}
    />
  );
};
